import React from 'react';
import { customFilter, FILTER_TYPES } from 'react-bootstrap-table2-filter';
import { Link } from 'react-router-dom';
import DatePicker from '../../components/datepicker';
import Input from '../../components/input';
import Select from '../../components/select';
import TableColumnFilter from '../../components/table-column-filter';
import { IBootstrapColumn } from '../../types/components/table/column-filter';
import {
    ITableColumn, ITableLayout, ITableModalAction, TTableColumnFilters,
} from '../../types/layout/table';
import { useWindowSize } from '../../hooks/ui/useWindowSize';

const getFilterComponent = (type?: TTableColumnFilters, props?: any) => {
    if (typeof type === 'string') {
        switch (type) {
            case 'datepicker':
            case 'datepicker-range': {
                return React.createElement(DatePicker, {
                    ...props,
                    type,
                });
            }
            case 'input': {
                return React.createElement(Input, props);
            }
            case 'select':
            case 'select-multiple': {
                return React.createElement(Select, props);
            }
        }
    }
    return null;
};

/**
 * xs: 0px,
 * sm: 576px,
 * md: 768px,
 * lg: 992px,
 * xl: 1200px,
 * xxl: 1400px,
 */
const getColumnWidth = (columnWidth: ITableColumn['width'], { width }: ReturnType<typeof useWindowSize>) => {
    if (typeof columnWidth === 'number') {
        return columnWidth;
    }
    if (columnWidth instanceof Object) {
        if (width < 576) {
            return columnWidth.xs;
        }
        if (width < 768) {
            return columnWidth.sm || columnWidth.xs;
        }
        if (width < 992) {
            return columnWidth.md || columnWidth.sm || columnWidth.xs;
        }
        if (width < 1200) {
            return columnWidth.lg || columnWidth.md || columnWidth.sm || columnWidth.xs;
        }
        if (width < 1400) {
            return columnWidth.xl || columnWidth.lg || columnWidth.md || columnWidth.sm || columnWidth.xs;
        }
        return columnWidth.xxl || columnWidth.xl || columnWidth.lg || columnWidth.md || columnWidth.sm || columnWidth.xs;
    }
    return undefined;
};

const getColumnHidden = (columnWidth: ITableColumn['width'], { width }: ReturnType<typeof useWindowSize>) => {
    if (typeof columnWidth === 'number') {
        return false;
    }
    if (columnWidth instanceof Object) {
        if (width < 576) {
            return !(columnWidth.xs);
        }
        if (width < 768) {
            return !(columnWidth.sm || columnWidth.xs);
        }
        if (width < 992) {
            return !(columnWidth.md || columnWidth.sm || columnWidth.xs);
        }
        if (width < 1200) {
            return !(columnWidth.lg || columnWidth.md || columnWidth.sm || columnWidth.xs);
        }
        if (width < 1400) {
            return !(columnWidth.xl || columnWidth.lg || columnWidth.md || columnWidth.sm || columnWidth.xs);
        }
        return !(columnWidth.xxl || columnWidth.xl || columnWidth.lg || columnWidth.md || columnWidth.sm || columnWidth.xs);
    }
    return false;
};

export const transformToBootstrapColumns = (tableLayout: ITableLayout, windowSize: ReturnType<typeof useWindowSize>, tableName: string): IBootstrapColumn[] => tableLayout.columns.map((item) => {
    const column = { ...item };

    let bootstrapColumn: IBootstrapColumn = {
        dataField: column.dataIndex,
        text: column.title,
        sort: column.sort,
        style: {
            fontSize: tableLayout.fontSize,
            width: getColumnWidth(column.width, windowSize),
        },
        headerStyle: {
            fontSize: tableLayout.fontSize,
            width: getColumnWidth(column.width, windowSize),
        },
        formatter: (cell, row: any) => (typeof tableLayout.actions?.edit === 'string' ? (
            <>
                <div dangerouslySetInnerHTML={{ __html: cell }} />
                <Link className="text-dark table-cell-link" to={tableLayout.actions.edit.replace('{objectId}', row[tableLayout.primaryKey])} />
            </>
        ) : <div dangerouslySetInnerHTML={{ __html: cell }} />),
        classes: column.hoverUnderline ? 'table-cell-hover-underline' : undefined,
    };

    if (typeof column.filter === 'string') {
        bootstrapColumn = {
            ...bootstrapColumn,
            filter: typeof column.filter === 'string' ? customFilter({ type: FILTER_TYPES.TEXT }) : undefined,
            filterRenderer: (onFilter: any) => (
                <TableColumnFilter column={column} onFilter={onFilter} tableName={tableName}>
                    {getFilterComponent(column.filter, { ...column.filterOptions, onFilter })}
                </TableColumnFilter>
            ),
        };
    }

    bootstrapColumn.hidden = column.hidden || getColumnHidden(column.width, windowSize);
    bootstrapColumn.hiddenBreakpointColumn = getColumnHidden(column.width, windowSize);

    return bootstrapColumn;
});

export const getCreateActionClassName = (action: string | ITableModalAction) => {
    if (action && typeof action === 'object' && typeof action.className === 'string') {
        return `btn btn-sm ${action.className}`;
    }
    return 'btn btn-sm btn-outline-secondary';
};
